(() => {
  if (window.PYKCarouselScroll || !document.body.classList.contains('pyk-halloween-on')) return;
  const selector = '[data-fc-viewport], [data-pd-viewport], [data-cc-viewport], [data-rv-viewport]';
  const instances = new WeakMap();
  const interactive = 'button, input, select, textarea, summary, [role="button"], [contenteditable], ooo-wl-wishlist-button';
  class ManualCarousel {
    constructor(viewport) {
      this.viewport = viewport;
      this.prefix = ['fc', 'pd', 'cc', 'rv'].find(key => viewport.hasAttribute(`data-${key}-viewport`));
      this.section = viewport.closest('.shopify-section') || viewport.parentElement;
      this.track = viewport.querySelector(`.${this.prefix}-track`);
      if (!this.track) return;
      this.managesArrows = this.prefix === 'fc' || this.prefix === 'pd';
      const animation = getComputedStyle(this.track);
      this.loop = this.managesArrows && (viewport.dataset.pykLoop === 'true' || (!viewport.hasAttribute('data-pyk-loop') && /marquee/.test(animation.animationName)));
      this.duration = Number(viewport.dataset.pykDuration) || parseFloat(animation.animationDuration) || 40;
      this.reverse = (viewport.dataset.pykDirection || animation.animationDirection) === 'reverse';
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.manual = !this.loop;
      this.abort = new AbortController();
      this.originalSnap = [viewport.style.getPropertyValue('scroll-snap-type'), viewport.style.getPropertyPriority('scroll-snap-type')];
      this.listen = (target, type, callback, options = {}) => target.addEventListener(type, callback, {...options, signal: this.abort.signal});
      viewport.classList.add('pyk-swipe-ready');
      if (this.managesArrows) {
        viewport.tabIndex = 0;
        viewport.setAttribute('role', 'group');
        if (!viewport.hasAttribute('aria-label')) viewport.setAttribute('aria-label', this.section.querySelector('section[aria-label]')?.getAttribute('aria-label') || document.title);
        this.section.querySelectorAll(`[data-${this.prefix}-prev], [data-${this.prefix}-next]`).forEach(button => {
          this.listen(button, 'click', () => this.step(button.hasAttribute(`data-${this.prefix}-prev`) ? -1 : 1));
          this.listen(button, 'mouseenter', () => { this.section.classList.add(`${this.prefix}-paused`); this.cancelResume(); });
          this.listen(button, 'mouseleave', () => { if (!this.manual) this.section.classList.remove(`${this.prefix}-paused`); this.scheduleResume(); });
        });
        this.listen(viewport, 'keydown', event => {
          if (event.target !== viewport || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault(); this.enterManual();
          if (event.key === 'Home') viewport.scrollTo({left: 0, behavior: this.motion.matches ? 'auto' : 'smooth'});
          else if (event.key === 'End') viewport.scrollTo({left: viewport.scrollWidth, behavior: this.motion.matches ? 'auto' : 'smooth'});
          else this.step(event.key === 'ArrowLeft' ? -1 : 1);
        });
        this.listen(viewport, 'focusin', () => { this.enterManual(); this.cancelResume(); });
        this.listen(viewport, 'focusout', event => { if (!viewport.contains(event.relatedTarget)) this.scheduleResume(); });
      }
      this.listen(viewport, 'wheel', event => {
        if (event.ctrlKey || !(Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY))) return;
        this.enterManual(); this.cancelResume();
        const delta = (event.deltaX || event.deltaY) * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientWidth : 1);
        const destination = Math.max(0, Math.min(viewport.scrollWidth - viewport.clientWidth, viewport.scrollLeft + delta));
        // Consume horizontal movement here so the first wheel packet is not
        // lost while the animation changes into a native scroll container.
        if (destination !== viewport.scrollLeft) { event.preventDefault(); viewport.scrollLeft = destination; }
        this.scheduleResume();
      }, {passive: false});
      this.listen(viewport, 'touchstart', () => { this.touching = true; this.enterManual(); this.cancelResume(); }, {passive: true});
      const touchEnd = () => { this.touching = false; this.scheduleResume(); };
      this.listen(viewport, 'touchend', touchEnd, {passive: true});
      this.listen(viewport, 'touchcancel', touchEnd, {passive: true});
      this.listen(viewport, 'pointerdown', event => {
        this.suppressClick = false;
        if (!['mouse', 'pen'].includes(event.pointerType) || event.button !== 0 || event.isPrimary === false || event.target.closest(interactive)) return;
        this.enterManual(); this.cancelResume();
        this.pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, start: viewport.scrollLeft, dragged: false};
      });
      this.listen(window, 'pointermove', event => {
        const pointer = this.pointer;
        if (!pointer || pointer.id !== event.pointerId) return;
        const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
        if (!pointer.dragged) {
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.endPointer(); return; }
          if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
          pointer.dragged = true; viewport.classList.add('pyk-swipe-dragging');
          viewport.style.setProperty('scroll-behavior', 'auto', 'important');
          viewport.style.setProperty('scroll-snap-type', 'none', 'important');
          try { viewport.setPointerCapture(event.pointerId); } catch {}
        }
        event.preventDefault(); viewport.scrollLeft = pointer.start - dx;
      }, {passive: false});
      this.listen(window, 'pointerup', event => { if (this.pointer?.id === event.pointerId) this.endPointer(); });
      this.listen(window, 'pointercancel', event => { if (this.pointer?.id === event.pointerId) this.endPointer(); });
      this.listen(window, 'blur', () => this.endPointer());
      this.listen(viewport, 'dragstart', event => { if (this.pointer) event.preventDefault(); });
      this.listen(viewport, 'click', event => {
        if (this.suppressClick && event.detail !== 0) { event.preventDefault(); event.stopImmediatePropagation(); this.suppressClick = false; }
      }, {capture: true});
      this.listen(viewport, 'scroll', () => { this.updateArrows(); this.scheduleResume(); }, {passive: true});
      this.listen(window, 'resize', () => this.updateArrows());
      this.motionChanged = () => { if (this.motion.matches) { this.enterManual(); this.cancelResume(); } else this.scheduleResume(); };
      this.motion.addEventListener?.('change', this.motionChanged);
      if (this.motion.matches) this.enterManual();
      this.updateArrows();
    }
    enterManual() {
      if (this.manual) return;
      const transform = getComputedStyle(this.track).transform;
      const parts = transform.match(/matrix(?:3d)?\(([^)]+)\)/)?.[1].split(',').map(Number);
      const offset = parts ? -(parts.length === 6 ? parts[4] : parts[12]) : 0;
      this.manual = true;
      this.section.classList.add(`${this.prefix}-manual`);
      this.viewport.style.setProperty('scroll-snap-type', 'none', 'important');
      this.viewport.scrollLeft = Math.max(0, offset);
      this.updateArrows();
    }
    step(direction) {
      this.enterManual(); this.cancelResume();
      const card = this.track.querySelector('.fc-slide, .pd-card, .pyk-card');
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      this.viewport.scrollBy({left: direction * ((card?.getBoundingClientRect().width || this.viewport.clientWidth * .9) + gap), behavior: this.motion.matches ? 'auto' : 'smooth'});
      this.scheduleResume();
    }
    cancelResume() { clearTimeout(this.timer); this.timer = undefined; }
    scheduleResume() {
      this.cancelResume();
      if (!this.loop || !this.manual || this.motion.matches || this.pointer || this.touching || this.viewport.contains(document.activeElement)) return;
      this.timer = setTimeout(() => this.resume(), 3000);
    }
    resume() {
      if (!this.manual || this.motion.matches || this.pointer || this.touching || this.viewport.contains(document.activeElement)) return;
      const offset = this.viewport.scrollLeft;
      this.section.classList.add(`${this.prefix}-paused`);
      this.section.classList.remove(`${this.prefix}-manual`);
      const style = getComputedStyle(this.track);
      const distance = (this.track.getBoundingClientRect().width + (parseFloat(style.columnGap || style.gap) || 0)) / 2;
      let fraction = distance > 0 ? ((offset / distance) % 1 + 1) % 1 : 0;
      if (this.reverse) fraction = (1 - fraction) % 1;
      this.track.style.animationDelay = `${-fraction * this.duration}s`;
      this.viewport.scrollLeft = 0;
      this.manual = false;
      this.restoreSnap();
      this.section.classList.remove(`${this.prefix}-paused`);
      this.updateArrows();
    }
    restoreSnap() {
      if (this.originalSnap[0]) this.viewport.style.setProperty('scroll-snap-type', ...this.originalSnap);
      else this.viewport.style.removeProperty('scroll-snap-type');
    }
    endPointer() {
      const pointer = this.pointer;
      if (!pointer) return;
      if (pointer.dragged) {
        this.suppressClick = true;
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 350);
      }
      try { if (this.viewport.hasPointerCapture(pointer.id)) this.viewport.releasePointerCapture(pointer.id); } catch {}
      this.pointer = undefined;
      this.viewport.classList.remove('pyk-swipe-dragging');
      this.viewport.style.removeProperty('scroll-behavior');
      if (!this.managesArrows || !this.loop) this.restoreSnap();
      this.scheduleResume();
    }
    updateArrows() {
      if (!this.managesArrows) return;
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      this.section.querySelectorAll(`[data-${this.prefix}-prev]`).forEach(button => { button.disabled = this.manual && this.viewport.scrollLeft <= 1; });
      this.section.querySelectorAll(`[data-${this.prefix}-next]`).forEach(button => { button.disabled = this.manual && this.viewport.scrollLeft >= max - 1; });
    }
    destroy() {
      this.cancelResume(); clearTimeout(this.clickTimer); this.abort.abort();
      this.motion.removeEventListener?.('change', this.motionChanged);
      this.viewport.classList.remove('pyk-swipe-ready', 'pyk-swipe-dragging');
      this.restoreSnap();
    }
  }
  function refresh(root = document) {
    const viewports = [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
    viewports.forEach(viewport => { if (!instances.has(viewport)) instances.set(viewport, new ManualCarousel(viewport)); });
  }
  document.addEventListener('shopify:section:load', event => refresh(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(selector).forEach(viewport => { instances.get(viewport)?.destroy(); instances.delete(viewport); }));
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))) refresh(node); }))).observe(document.body, {childList: true, subtree: true});
  window.PYKCarouselScroll = {refresh};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(), {once: true});
  else refresh();
})();
